import { ColumnDef } from "@tanstack/react-table";
import { HeaderSortable } from "@/components/Base/HeaderSortable";
import { Badge, BadgeProps } from "@/components/Base/Badge";
import { formatHours } from "@/lib/formatHours";
import { JobEntry, JobState } from "@/lib/jobState";
import { JOB_DETAIL_COLUMN_TOOLTIPS } from "./columnTooltips";

const HEADER_LABEL_CLASSNAME =
  "text-xs font-semibold uppercase tracking-wide text-slate-500 hover:text-slate-500";

const STATE_BADGE: Record<
  JobState,
  { label: string; variant: BadgeProps["variant"] }
> = {
  pending: { label: "Pending", variant: "outline" },
  approved: { label: "Approved", variant: "secondary" },
  completed: { label: "Completed", variant: "success" },
  declined: { label: "Declined", variant: "destructive" },
};

export const employeeJobsColumns = (): ColumnDef<JobEntry>[] => [
  {
    accessorKey: "cardNumber",
    // Identifies which repair order a row belongs to -- keep it out of the
    // Columns menu, the same way Technician is pinned on the parent table.
    enableHiding: false,
    meta: { label: "RO #" },
    header: ({ column }) => (
      <HeaderSortable
        column={column}
        title="RO #"
        labelClassName={HEADER_LABEL_CLASSNAME}
        tooltip={JOB_DETAIL_COLUMN_TOOLTIPS.cardNumber}
      />
    ),
    cell: ({ row }) => (
      <span className="text-sm text-slate-600">
        {row.original.cardNumber ?? "—"}
      </span>
    ),
  },
  {
    accessorKey: "jobName",
    meta: {
      label: "Job",
      // Job names are free text and routinely long; cap the column so it
      // ellipsizes rather than squeezing State and Hours off the panel.
      getCellClass: () => "max-w-[10rem]",
    },
    header: ({ column }) => (
      <HeaderSortable
        column={column}
        title="Job"
        labelClassName={HEADER_LABEL_CLASSNAME}
        tooltip={JOB_DETAIL_COLUMN_TOOLTIPS.jobName}
      />
    ),
    cell: ({ row }) => (
      <span
        className="block truncate text-sm font-medium text-slate-700"
        title={row.original.jobName ?? undefined}
      >
        {row.original.jobName ?? "—"}
      </span>
    ),
  },
  {
    accessorKey: "state",
    meta: { label: "State" },
    header: ({ column }) => (
      <HeaderSortable
        column={column}
        title="State"
        labelClassName={HEADER_LABEL_CLASSNAME}
        tooltip={JOB_DETAIL_COLUMN_TOOLTIPS.state}
      />
    ),
    cell: ({ row }) => {
      const badge = STATE_BADGE[row.original.state];
      return (
        <Badge variant={badge.variant} type="small">
          {badge.label}
        </Badge>
      );
    },
  },
  {
    id: "hours",
    accessorFn: (row) => row.hours,
    meta: {
      label: "Hours",
      getCellClass: () => "text-right tabular-nums text-slate-600",
    },
    header: ({ column }) => (
      <HeaderSortable
        column={column}
        title="Hours"
        labelClassName={HEADER_LABEL_CLASSNAME}
        tooltip={JOB_DETAIL_COLUMN_TOOLTIPS.hours}
      />
    ),
    sortingFn: (a, b) => a.original.hours - b.original.hours,
    cell: ({ row }) => formatHours(row.original.hours),
  },
];
